import { useMemo, useState } from 'react';
import './ledger.css';
import { demo } from '../data/demo';
import { NewInvoice } from '../components/CreateForms';
import { invoicesToCsv, downloadCsv } from '../lib/commercial';
import { BACKEND } from '../lib/db';
import { onFail } from '../lib/errors';
import { TENANT } from '@tenant';

/* =====================================================================================
   Ledger — quote to cash and credit control.

   Credit control is the part small companies do worst, which is why cash is always a
   surprise. Two things here that most systems omit:

   1. Promise-to-pay tracking. Whether a customer keeps a promise is the single most
      predictive credit control metric and almost nobody records it.
   2. Disputes pause dunning. Chasing a disputed invoice with an automated reminder is how
      you turn a query into a relationship problem.
   ===================================================================================== */

type Status = 'draft' | 'issued' | 'sent' | 'part_paid' | 'paid' | 'overdue' | 'disputed' | 'written_off';

interface Invoice {
  id: string; number: string; account: string; country: string;
  issued: string; due: string;
  currency: string; total: number; paid: number;
  status: Status;
  disputed?: boolean; disputeReason?: string;
  dunningStage: number; lastChased?: string;
  promise?: { amount: number; date: string; by: string; status: 'open' | 'kept' | 'broken' };
  postedToSage: boolean;
}

/* Live, not fixed. A pinned date makes every ageing bucket drift by a day, every day. */
const TODAY = new Date();
const days = (d: string) => Math.round((TODAY.getTime() - new Date(d).getTime()) / 86_400_000);

/* The tenant's own receivables win. demo() still gates the inline examples, which belong to
   the transplant tenant; a tenant supplying its own invoices gets those instead of nothing. */
const TENANT_INVOICES = TENANT.seed.invoice as Invoice[];

const INVOICES: Invoice[] = TENANT_INVOICES.length ? TENANT_INVOICES : TENANT_INVOICES;

const RATES: Record<string, number> = { GBP: 1, EUR: 0.86, USD: 0.79, SEK: 0.075 };
const base = (n: number, c: string) => n * (RATES[c] ?? 1);
const money = (n: number, c: string) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: c, maximumFractionDigits: 0 }).format(n);

const DUNNING = demo([
  { step: 1, at: -7, action: 'Courtesy reminder', tone: 'Friendly' },
  { step: 2, at: 0, action: 'Due today notice', tone: 'Neutral' },
  { step: 3, at: 7, action: 'First chase', tone: 'Firm' },
  { step: 4, at: 14, action: 'Statement of account', tone: 'Firm' },
  { step: 5, at: 30, action: 'Final notice before escalation', tone: 'Formal' },
  { step: 6, at: 45, action: 'Credit hold and escalation', tone: 'Formal' },
]);

export function LedgerView() {
  const [creating, setCreating] = useState(false);
  const [tab, setTab] = useState<'ageing' | 'invoices' | 'dunning'>('ageing');

  const m = useMemo(() => {
    const outstanding = INVOICES.filter((i) => !['paid', 'draft', 'written_off'].includes(i.status));
    const total = outstanding.reduce((a, i) => a + base(i.total - i.paid, i.currency), 0);
    const overdue = outstanding.filter((i) => days(i.due) > 0);
    const overdueValue = overdue.reduce((a, i) => a + base(i.total - i.paid, i.currency), 0);
    const disputed = outstanding.filter((i) => i.disputed);
    const disputedValue = disputed.reduce((a, i) => a + base(i.total - i.paid, i.currency), 0);

    // DSO, simple version: outstanding divided by average daily billings over the period.
    const billed = INVOICES.reduce((a, i) => a + base(i.total, i.currency), 0);
    const dso = billed ? Math.round((total / billed) * 180) : 0;

    const promises = INVOICES.filter((i) => i.promise);
    const kept = promises.filter((i) => i.promise!.status === 'kept').length;
    const broken = promises.filter((i) => i.promise!.status === 'broken').length;

    const buckets = { current: 0, d30: 0, d60: 0, d90: 0, d90p: 0 };
    for (const i of outstanding) {
      const v = base(i.total - i.paid, i.currency);
      const d = days(i.due);
      if (d <= 0) buckets.current += v;
      else if (d <= 30) buckets.d30 += v;
      else if (d <= 60) buckets.d60 += v;
      else if (d <= 90) buckets.d90 += v;
      else buckets.d90p += v;
    }
    return { total, overdueValue, disputedValue, dso, buckets, kept, broken, unposted: INVOICES.filter((i) => !i.postedToSage && i.status !== 'draft').length };
  }, []);

  const maxBucket = Math.max(...Object.values(m.buckets), 1);

  return (
    <div className="page ledger">
      <div className="page-head">
        <h1 className="page-title">Ledger</h1>
        <p className="page-sub">
          Quote to cash and credit control. Issued invoices are immutable, corrections are credit
          notes, and Sage remains the statutory filing endpoint rather than the operational truth.
        </p>
      </div>

      {INVOICES.length === 0 && (
        <p className="module-empty">No invoices yet. New invoices start as drafts, because an issued invoice is append-only: it can be credited but never edited.</p>
      )}

      <div className="toolbar">
        <div className="seg">
          {(['ageing', 'invoices', 'dunning'] as const).map((t) => (
            <button key={t} className={tab === t ? 'on' : ''} onClick={() => setTab(t)}>
              {t === 'ageing' ? 'Ageing' : t === 'invoices' ? 'Invoices' : 'Dunning ladder'}
            </button>
          ))}
        </div>
        {/* Not Sage-specific: the accounting package might be Xero, QuickBooks or NetSuite,
            and every one of them imports a CSV. */}
        <button className="btn" style={{ marginLeft: 'auto' }}
                onClick={() => downloadCsv(
                  `invoices-${new Date().toISOString().slice(0, 10)}.csv`,
                  invoicesToCsv(INVOICES as any))}>Export for accounts</button>
        {BACKEND === 'supabase' && (
          /* The one-way push to the ledger of record. Unposted issued invoices go out as
             Sage 50 Audit Trail rows; marking them posted stamps accounting_ref so a row
             can never be imported twice. */
          <button className="btn" onClick={async () => {
            try {
              const { supabase } = await import('../lib/supabase');
              const { data, error } = await supabase().schema('ledger')
                .from('v_sage_invoice_rows').select('*');
              if (error) throw new Error(error.message);
              const rows = (data ?? []) as Record<string, unknown>[];
              if (!rows.length) { window.alert('Nothing waiting for Sage: every issued invoice is already posted.'); return; }
              const head = 'Type,Account Reference,Nominal A/C Ref,Date,Reference,Details,Net Amount,Tax Code,Tax Amount';
              const csv = [head, ...rows.map((r) =>
                [r.type, r.account_ref, r.nominal, r.tx_date, r.reference,
                 String(r.details ?? '').replace(/,/g, ' '), r.net, r.tax_code, r.tax].join(','))].join('\n');
              const batch = 'SAGE-' + new Date().toISOString().slice(0, 10);
              downloadCsv(`${batch}.csv`, csv);
              if (window.confirm(`${rows.length} line(s) exported. Mark them posted as ${batch}? `
                  + 'Do this once the file has imported into Sage cleanly.')) {
                const { data: n, error: e2 } = await supabase().schema('ledger')
                  .rpc('mark_posted_to_sage', { p_batch: batch });
                if (e2) throw new Error(e2.message);
                window.alert(`${n} invoice(s) marked posted as ${batch}.`);
              }
            } catch (e) { onFail('The Sage export')(e as Error); }
          }}>Export to Sage</button>
        )}
        <button className="btn primary" onClick={() => setCreating(true)}>New invoice</button>
      </div>

      <div className="metrics">
        <div className="metric card">
          <div className="label">Outstanding</div>
          <div className="metric-value">{money(m.total, 'GBP')}</div>
        </div>
        <div className="metric card" style={{ borderColor: 'var(--warn)', background: 'var(--warn-soft)' }}>
          <div className="label">Overdue</div>
          <div className="metric-value" style={{ color: 'var(--warn)' }}>{money(m.overdueValue, 'GBP')}</div>
        </div>
        <div className="metric card">
          <div className="label">Days sales outstanding</div>
          <div className="metric-value">{m.dso}</div>
          <div className="metric-foot"><span>Rolling estimate</span></div>
        </div>
        <div className="metric card">
          <div className="label">Promises kept</div>
          <div className="metric-value">{m.kept}<span className="of">/{m.kept + m.broken}</span></div>
          <div className="metric-foot"><span>The most predictive metric</span></div>
        </div>
      </div>

      {m.unposted > 0 && (
        <div className="insight card">
          <strong>{m.unposted} issued invoice{m.unposted > 1 ? 's' : ''} not yet posted to Sage.</strong>
          <span>
            Forum is the operational truth and Sage is the statutory filing endpoint. Divergence
            between them is a reconciliation problem waiting for month end.
          </span>
        </div>
      )}

      {tab === 'ageing' && (
        <>
          <div className="card ageing">
            <div className="label" style={{ padding: 'var(--s4) var(--s4) 0' }}>Ageing profile</div>
            <div className="ageing-chart">
              {[
                { k: 'Not yet due', v: m.buckets.current, tone: 'ok' },
                { k: '1 to 30 days', v: m.buckets.d30, tone: 'info' },
                { k: '31 to 60', v: m.buckets.d60, tone: 'warn' },
                { k: '61 to 90', v: m.buckets.d90, tone: 'warn' },
                { k: 'Over 90', v: m.buckets.d90p, tone: 'risk' },
              ].map((b) => (
                <div key={b.k} className="ageing-col">
                  <div className="ageing-value">{b.v ? money(b.v, 'GBP') : '—'}</div>
                  <div className="ageing-bar-wrap">
                    <div className={`ageing-bar tone-${b.tone}`} style={{ height: `${(b.v / maxBucket) * 100}%` }} />
                  </div>
                  <div className="ageing-label">{b.k}</div>
                </div>
              ))}
            </div>
          </div>

          {m.disputedValue > 0 && (
            <div className="card dispute-note">
              <strong>{money(m.disputedValue, 'GBP')} is in dispute and dunning is paused on it.</strong>
              <span>
                Chasing a disputed invoice with an automated reminder turns a query into a
                relationship problem. Resolve the query, then resume the ladder.
              </span>
            </div>
          )}
        </>
      )}

      {tab === 'invoices' && (
        <div className="card table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Invoice</th><th>Account</th><th className="num">Total</th>
                <th className="num">Outstanding</th><th className="num">Due</th>
                <th className="num">Age</th><th>Status</th><th>Credit control</th>
              </tr>
            </thead>
            <tbody>
              {INVOICES.map((i) => {
                const age = days(i.due);
                return (
                  <tr key={i.id}>
                    <td>
                      <div className="cell-title mono">{i.number}</div>
                      <div className="cell-sub">{i.postedToSage ? 'posted' : 'not posted to Sage'}</div>
                    </td>
                    <td>
                      <div className="cell-title">{i.account}</div>
                      <div className="cell-sub mono">{i.country}</div>
                    </td>
                    <td className="num">{money(i.total, i.currency)}</td>
                    <td className="num">
                      {i.total - i.paid ? money(i.total - i.paid, i.currency) : <span className="muted">—</span>}
                    </td>
                    <td className="num">{i.due}</td>
                    <td className="num">
                      {i.status === 'paid' ? <span className="muted">—</span>
                        : <span className={age > 30 ? 'age-bad' : age > 0 ? 'age-warn' : ''}>{age > 0 ? `+${age}` : age}</span>}
                    </td>
                    <td>
                      <span className={`pill ${
                        i.status === 'paid' ? 'ok'
                          : i.status === 'disputed' ? 'risk'
                          : i.status === 'overdue' ? 'risk'
                          : i.status === 'part_paid' ? 'warn' : 'mute'}`}>
                        {i.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td>
                      {i.disputed && <div className="cc-note risk">Dunning paused: {i.disputeReason}</div>}
                      {i.promise && (
                        <div className={`cc-note ${i.promise.status === 'broken' ? 'risk' : 'ok'}`}>
                          Promise {i.promise.status}: {money(i.promise.amount, i.currency)} by {i.promise.date} ({i.promise.by})
                        </div>
                      )}
                      {!i.disputed && !i.promise && i.dunningStage > 0 && (
                        <div className="cc-note">At step {i.dunningStage}, last chased {i.lastChased}</div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'dunning' && (
        <div className="card ladder">
          <div className="label" style={{ marginBottom: 'var(--s3)' }}>Dunning ladder</div>
          {DUNNING.map((d) => (
            <div key={d.step} className="ladder-step">
              <div className="ladder-num">{d.step}</div>
              <div className="ladder-when">
                {d.at < 0 ? `${Math.abs(d.at)} days before due` : d.at === 0 ? 'On due date' : `${d.at} days overdue`}
              </div>
              <div className="ladder-action">{d.action}</div>
              <span className="pill mute">{d.tone}</span>
            </div>
          ))}
          <div className="table-note">
            Correspondence is generated from the contract's actual payment terms, not a generic
            template, so a customer on 60-day terms is never chased on day 31. Disputed invoices are
            removed from the ladder until the dispute is closed.
          </div>
        </div>
      )}
      {creating && <NewInvoice onClose={() => setCreating(false)} onDone={() => setCreating(false)} />}

    </div>
  );
}
